import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import type { ReactNode } from "react";
import { App as AntApp, ConfigProvider, Layout } from "antd";
import Dashboard from "./pages/Dashboard";
import ExecDashboard from "./pages/ExecDashboard";
import AgencyIntake from "./pages/AgencyIntake";
import VendorIntake from "./pages/VendorIntake";
import Projects from "./pages/Projects";
import Intakes from "./pages/Intakes";
import Board from "./pages/Board";
import Vendors from "./pages/Vendors";
import Settings from "./pages/Settings";
import RequestAccess from "./pages/RequestAccess";
import TopNav from "./components/TopNav";
import ProtectedRoute from "./components/ProtectedRoute";
import BetaGate from "./components/BetaGate";
import { CommandPalette } from "./components/CommandPalette";
import { ApexProvider, useApex } from "./components/apex/ApexProvider";
import ApexBubble from "./components/apex/ApexBubble";
import ApexDrawer from "./components/apex/ApexDrawer";
import { AuthProvider, useAuth } from "./lib/auth";
import { ditTheme } from "./lib/theme";
import SignIn from "./pages/SignIn";
import OAuthConsent from "./pages/OAuthConsent";
import dashboardBg from "./assets/dashboard-background.webp";

function AppLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { drawerOpen, apexWidth, pushMode } = useApex();
  const hideTopNav =
    location.pathname === "/agency" ||
    location.pathname === "/vendor" ||
    location.pathname === "/request-access" ||
    location.pathname === "/oauth/consent";
  const showBackground = location.pathname === "/signin";

  // Push the layout aside when Apex is open AND the viewport is wide enough
  // to comfortably fit content next to it. On narrow viewports the panel
  // overlays instead so content doesn't get crushed.
  const apexOffset = drawerOpen && pushMode ? apexWidth : 0;

  return (
    <Layout
      style={{
        minHeight: "100vh",
        marginRight: apexOffset,
        transition: "margin-right 0.2s ease",
      }}
    >
      {hideTopNav ? null : <TopNav />}
      <Layout.Content
        style={{
          padding: 24,
          ...(showBackground && {
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.5)), url(${dashboardBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          }),
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>{children}</div>
      </Layout.Content>
    </Layout>
  );
}

/**
 * `/` lands the executive role on `/exec`; everyone else sees the operator
 * Dashboard. Kept inline so we don't need to plumb role state through to App.
 */
function HomeRoute() {
  const { isExec } = useAuth();
  if (isExec) return <Navigate to="/exec" replace />;
  return <Dashboard />;
}

function App() {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  // Global keyboard shortcut for Cmd/Ctrl+Shift+K
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === "k") {
        event.preventDefault();
        setCommandPaletteOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <ConfigProvider theme={ditTheme}>
      <AuthProvider>
        <AntApp>
          <BrowserRouter>
            <ApexProvider>
              <AppLayout>
                <CommandPalette
                  open={commandPaletteOpen}
                  onClose={() => setCommandPaletteOpen(false)}
                />
                <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <HomeRoute />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exec"
                  element={
                    <ProtectedRoute>
                      <ExecDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route path="/signin" element={<SignIn />} />
                {/* OAIP-153 (interim): one-click OAuth consent. Not behind
                    ProtectedRoute — it renders its own sign-in for users who
                    aren't signed into Apex in this browser. */}
                <Route path="/oauth/consent" element={<OAuthConsent />} />
                <Route path="/request-access" element={<RequestAccess />} />
                <Route
                  path="/agency"
                  element={
                    <BetaGate route="agency">
                      <AgencyIntake />
                    </BetaGate>
                  }
                />
                <Route
                  path="/vendor"
                  element={
                    <BetaGate route="vendor">
                      <VendorIntake />
                    </BetaGate>
                  }
                />
                <Route
                  path="/projects"
                  element={
                    <ProtectedRoute>
                      <Projects />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/intakes"
                  element={
                    <ProtectedRoute>
                      <Intakes />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/board"
                  element={
                    <ProtectedRoute>
                      <Board />
                    </ProtectedRoute>
                  }
                />
                <Route path="/tasks" element={<Navigate to="/board" replace />} />
                <Route
                  path="/vendors"
                  element={
                    <ProtectedRoute>
                      <Vendors />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route path="/intake/agency" element={<Navigate to="/agency" replace />} />
                <Route path="/intake/vendor" element={<Navigate to="/vendor" replace />} />
              </Routes>
              </AppLayout>
              {/* Apex bubble + drawer mount once at the app root so they're available on every page. */}
              <ApexBubble />
              <ApexDrawer />
            </ApexProvider>
          </BrowserRouter>
        </AntApp>
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
